import React, {
	ChangeEvent,
	useState,
	useReducer,
	FormEvent,
	useEffect,
} from 'react';
import { text, unitList } from '../data';
import PartSearch from '../../components/bearing-search/components/PartSearch';
import {
	BearingInfo,
	TimkenUnit,
} from '../../components/bearing-search/types/types';
import partReducer, {
	BearingPartState,
	initialBearingPartState,
} from '../../components/bearing-search/reducers/reducer';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import SelectDropdown from '../../components/bearing-search/components/forms/SelectDropdown';
import clearanceCalculationsReducer, {
	initialClearanceCalculationsState,
} from './reducer';
import ClearanceForm from './clearance-form/ClearanceForm';
import {
	getPartStateFromParams,
	updateToolParams,
} from '../utils/engineering-tools';
import { FittingCalculationResults } from '../types/types';
import { trackStart } from '../utils/analytics';

const ClearanceCalculations: React.FC = () => {
	const [ isPartOpen, setIsPartOpen ] = useState( false );
	const [ searchInput, setSearchInput ] = useState( '' );
	const [ savedFittings, setSavedFittings ] = useState<
		Partial<FittingCalculationResults> | undefined
	>();
	const [ partState, partDispatch ] = useReducer(
		partReducer,
		initialBearingPartState,
	);
	const [ formState, dispatch ] = useReducer(
		clearanceCalculationsReducer,
		initialClearanceCalculationsState,
	);

	// Parse tool parameters if it's from recommended link
	useEffect( () => {
		trackStart( 'bearingClearanceCalculations' );

		const queryMap = new URLSearchParams( document.location.search );
		const savedPart = getPartStateFromParams( queryMap );

		if ( savedPart.partNumber ) {
			partDispatch( {
				type: 'reset',
				payload: savedPart,
			} );

			setSearchInput( savedPart.marketPlaceDescription || '' );
		}
	}, [] );

	// Pass down part number to next recommended tool
	useEffect( () => {
		updateToolParams<BearingPartState>( partState );

		return () => {
			updateToolParams();
		};
	}, [ partState.marketPlaceDescription ] );

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		const value = event.target.value;
		setSearchInput( value );
	};

	const verifyPart = ( part?: BearingInfo ) => {
		if ( part ) {
			partDispatch( {
				type: 'select_part',
				payload: part,
			} );

			setSearchInput( part.marketPlaceDescription || '' );
		}

		setIsPartOpen( false );
	};

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();

		if ( ! partState.partNumber ) {
			// Search Part
			setIsPartOpen( true );
		}
	};

	const clearSearch = () => {
		setSearchInput( '' );

		partDispatch( {
			type: 'reset',
		} );

		dispatch( {
			type: 'reset',
		} );
	};

	return (
		<>
			<div>
				<form onSubmit={handleSubmit}>
					<div className="t-engineering-tools__form-fields">
						<TextInput
							id="clearance_calculation_part_number"
							required={true}
							label={text.partNumber}
							verified={!! partState.partNumber}
							name="partNumber"
							value={searchInput}
							disabled={!! partState.partNumber}
							placeholder={text.partNumber}
							message={
								partState.partNumber
									? `${ text.bearingType }: ${ partState.bearingTypeLabel }`
									: text.partWildCardHint
							}
							onChange={inputPartNumber}
						/>

						{! partState.partNumber && (
							<button
								className="btn btn--primary"
								type="submit"
								disabled={! searchInput}
							>
								{text.lookup}
							</button>
						)}

						{!! partState.partNumber && (
							<SelectDropdown
								id="clearance_calculation_unit"
								name="unit"
								required={true}
								label={text.displayUnits}
								value={formState.unit}
								options={unitList}
								onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
									dispatch( {
										type: 'change_unit',
										payload: event.target.value as TimkenUnit,
									} )
								}
							/>
						)}
					</div>
				</form>
			</div>

			{!! partState.bearingType && (
				<ClearanceForm
					unit={formState.unit}
					bearing={partState}
					fittings={savedFittings}
					onClear={clearSearch}
				/>
			)}

			{isPartOpen && !! searchInput && (
				<PartSearch partNumber={searchInput} onSelect={verifyPart} onClose={() => setIsPartOpen( false )} />
			)}
		</>
	);
};

export default ClearanceCalculations;
